import { navigate } from "@reach/router";
import { LoadingSpinner } from "components/Loading/LoadingSpinner";
import { useCurrentCompanyId } from "hooks/auth";
import { useEffect, useState } from "react";
import { showToast } from "utils/helpers";

import CreateRfxTemplate from "newui/features/Rfx/Creation/CreateRFxTemplate";
import { useRfxSubmissionData } from "newui/features/Rfx/data-provider/RfxSubmissionDataProvider";

import { getTemplateById } from "../../../actions/RFQ/templateAction";

type ProtectedTemplateRouteProps = {
	isEdit?: boolean;
	isTemplate?: boolean;
	templateId: string;
};

export const ProtectedTemplateRoute = ({
	templateId,
	isEdit = false,
}: ProtectedTemplateRouteProps) => {
	const [isLoading, setIsLoading] = useState(true);
	const [isAuthorized, setIsAuthorized] = useState(false);
	const currentCompanyId = useCurrentCompanyId();
	const { updateLocalRfxData } = useRfxSubmissionData();

	useEffect(() => {
		const checkTemplateAccess = async () => {
			try {
				const template = await getTemplateById(parseInt(templateId));

				if (!template) {
					showToast("Template not found", "Error");
					navigate(`/account/${currentCompanyId}/manage-templates`);
					return;
				}

				// Check if template is locked and user is trying to edit
				if (isEdit && template.status === "locked") {
					showToast("Cannot edit locked template", "Access Denied");
					navigate(`/account/${currentCompanyId}/manage-templates`);
					return;
				}

				setIsAuthorized(true);
			} catch (error) {
				console.error("Template access check failed:", error);
				showToast("Failed to verify template access", "Error");
				navigate(`/account/${currentCompanyId}/manage-templates`);
			} finally {
				setIsLoading(false);
			}
		};

		if (templateId) {
			checkTemplateAccess();
		}
	}, [templateId, isEdit, currentCompanyId, updateLocalRfxData]);

	if (isLoading) {
		return (
			<div className="flex h-[50vh] items-center justify-center">
				<LoadingSpinner />
			</div>
		);
	}

	if (!isAuthorized) {
		return null; // Navigation already handled in useEffect
	}

	return (
		<CreateRfxTemplate
			isEdit={isEdit}
			rfxTemplateRouteId={templateId}
		/>
	);
};
